/**
 * LegalModal — the in-app Legal & Safety center.
 *
 * Two-pane reader (left rail: Privacy / Safety / Terms / Support) +
 * scrolling content. Reachable from the landing footer, Settings ▸
 * Legal, the profile menu, and signup / consent-gate Terms links.
 *
 * Visual contract (Drive design 2026-06-22):
 *   - Modal centered, ~960px wide, ~80vh tall
 *   - Title "Legal & Safety" + close X
 *   - Left rail: 4 doc tabs with icon + label; active highlights with
 *     a subtle sun tint + clay icon
 *   - Right: scrollable content with the doc's lede + sections; uses
 *     the themed .pc-scroll scrollbar from chrome/global.css
 *   - Below 600px (a phone upright, a narrow iPad window) the rail becomes
 *     a row of four tabs above full-width content: beside a 232px rail the
 *     text was a column about 70px wide (iPhone compact pass, 2026-10-25).
 *
 * Backs launch-compliance R6.
 */

import { useEffect, useState, type CSSProperties } from "react";
import { ChromeTokens } from "../chrome/tokens";
import { NARROW_BAR_MAX, useMaxWidth } from "../chrome/useNarrowBar";
import { Icon, type IconName } from "../chrome/Icon";
import { LEGAL_DOCS, type LegalDocId } from "./docs";
import { KidSafeSeal } from "./KidSafeSeal";

export interface LegalModalProps {
  open: boolean;
  doc: LegalDocId;
  onClose: () => void;
  /** The public /privacy and /terms pages: the kidSAFE seal and its wording
   *  under those two docs. Off everywhere else the modal opens, which
   *  includes signup and the consent gate, where children type. */
  seal?: boolean;
}

// Doc id → Icon name + display label. Single place to keep the rail
// labels in lockstep with the design (which uses "Safety (COPPA)" vs
// the docs file's "Safety & COPPA").
// `short` is the tab's label on a phone, where the four share one row.
const RAIL_LABELS: Record<LegalDocId, { label: string; short: string; icon: IconName }> = {
  privacy: { label: "Privacy Policy", short: "Privacy", icon: "lock" },
  safety: { label: "Safety (COPPA)", short: "Safety", icon: "shield" },
  terms: { label: "Terms of Service", short: "Terms", icon: "book-open" },
  support: { label: "Support", short: "Support", icon: "life-buoy" },
};

export function LegalModal({ open, doc, onClose, seal = false }: LegalModalProps): JSX.Element | null {
  const [active, setActive] = useState<LegalDocId>(doc);
  const compact = useMaxWidth(NARROW_BAR_MAX);

  // Re-sync when caller opens the modal pointed at a different doc.
  useEffect(() => {
    if (open) setActive(doc);
  }, [open, doc]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  const current = LEGAL_DOCS.find((d) => d.id === active) || LEGAL_DOCS[0];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Legal & Safety center"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={compact ? scrimCompactStyle : scrimStyle}
    >
      <div style={compact ? panelCompactStyle : panelStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header: title + close */}
        <header style={headerStyle}>
          <h2 style={titleStyle}>Legal &amp; Safety</h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={closeBtnStyle}
          >
            <Icon name="x" size={20} stroke={ChromeTokens.color.inkMuted} />
          </button>
        </header>

        {/* Body: rail + content */}
        <div style={compact ? bodyCompactStyle : bodyStyle}>
          <aside aria-label="Legal documents" style={compact ? railCompactStyle : railStyle}>
            {LEGAL_DOCS.map((d) => {
              const isActive = active === d.id;
              const meta = RAIL_LABELS[d.id];
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => setActive(d.id)}
                  aria-current={isActive ? "page" : undefined}
                  aria-label={compact ? meta.label : undefined}
                  style={compact ? railTabStyle(isActive) : railRowStyle(isActive)}
                >
                  {!compact && (
                    <Icon
                      name={meta.icon}
                      size={16}
                      stroke={isActive ? ChromeTokens.color.clay : ChromeTokens.color.inkMuted}
                    />
                  )}
                  <span>{compact ? meta.short : meta.label}</span>
                </button>
              );
            })}
          </aside>

          <section
            className="pc-scroll"
            aria-labelledby="legal-doc-title"
            style={compact ? contentCompactStyle : contentStyle}
          >
            <p style={ledeStyle}>{current.lede}</p>
            <p style={updatedStyle}>Last updated: {current.updated}</p>
            {current.sections.map((s, i) => (
              <section key={i} style={{ marginTop: 24 }}>
                <h3 style={sectionHStyle}>{s.h}</h3>
                {s.p?.map((p, j) => (
                  <p key={j} style={sectionPStyle}>
                    {p}
                  </p>
                ))}
                {s.ul && (
                  <ul style={ulStyle}>
                    {s.ul.map((li, j) => (
                      <li key={j} style={liStyle}>
                        {li}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
            {seal && (current.id === "privacy" || current.id === "terms") && (
              <KidSafeSeal wording style={{ marginTop: 32 }} />
            )}
            <footer style={footerStyle}>
              <address style={{ fontStyle: "normal", margin: 0, lineHeight: 1.5 }}>
                world by me Inc.
                <br />
                202 Walton Way Ste 192 Unit 744
                <br />
                Cedar Park, TX 78613
              </address>
              <p style={{ margin: "8px 0 0" }}>support@worldby.me</p>
            </footer>
          </section>
        </div>
      </div>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────

const scrimStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 1500,
  background: "rgba(47, 58, 69, 0.42)",
  display: "grid",
  placeItems: "center",
  // Clear of the notch and home indicator on a phone on its side, too.
  padding:
    "max(24px, var(--safe-top, 0px)) max(24px, var(--safe-right, 0px)) " +
    "max(24px, var(--safe-bottom, 0px)) max(24px, var(--safe-left, 0px))",
  fontFamily: ChromeTokens.font.ui,
};

// A phone: a smaller margin, kept clear of the status bar and home indicator.
const scrimCompactStyle: CSSProperties = {
  ...scrimStyle,
  padding:
    "max(12px, var(--safe-top, 0px)) max(12px, var(--safe-right, 0px)) " +
    "max(12px, var(--safe-bottom, 0px)) max(12px, var(--safe-left, 0px))",
};

const panelStyle: CSSProperties = {
  width: "min(960px, 100%)",
  height: "min(640px, calc(100vh - 48px))",
  background: ChromeTokens.color.base,
  borderRadius: ChromeTokens.radius.lg,
  boxShadow: ChromeTokens.shadow.lifted,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const panelCompactStyle: CSSProperties = {
  ...panelStyle,
  width: "100%",
  height: "100%",
  maxHeight: 640,
};

const headerStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 12px 10px 22px",
  borderBottom: `1px solid ${ChromeTokens.color.baseMuted}`,
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontFamily: ChromeTokens.font.display,
  fontSize: 22,
  fontWeight: 700,
  color: ChromeTokens.color.ink,
};

// 44pt at every width: a phone on its side keeps the rail, and a finger.
const closeBtnStyle: CSSProperties = {
  background: "transparent",
  border: "none",
  cursor: "pointer",
  width: 44,
  height: 44,
  padding: 0,
  borderRadius: 999,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: ChromeTokens.color.inkMuted,
};

const bodyStyle: CSSProperties = {
  flex: 1,
  display: "flex",
  minHeight: 0, // critical: allows the scrollable child to actually overflow + scroll
};

const railStyle: CSSProperties = {
  width: 232,
  flexShrink: 0,
  padding: 16,
  borderRight: `1px solid ${ChromeTokens.color.baseMuted}`,
  display: "flex",
  flexDirection: "column",
  gap: 4,
  overflowY: "auto",
};

const bodyCompactStyle: CSSProperties = {
  ...bodyStyle,
  flexDirection: "column",
};

const railCompactStyle: CSSProperties = {
  display: "flex",
  gap: 4,
  padding: "8px 10px",
  borderBottom: `1px solid ${ChromeTokens.color.baseMuted}`,
  flexShrink: 0,
};

function railTabStyle(active: boolean): CSSProperties {
  return {
    ...railRowStyle(active),
    flex: 1,
    minWidth: 0,
    minHeight: 44,
    padding: "0 4px",
    justifyContent: "center",
    textAlign: "center",
    whiteSpace: "nowrap",
  };
}

function railRowStyle(active: boolean): CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    minHeight: 44,
    background: active
      ? `color-mix(in srgb, ${ChromeTokens.color.sun} 30%, ${ChromeTokens.color.base})`
      : "transparent",
    border: "none",
    borderRadius: ChromeTokens.radius.md,
    cursor: "pointer",
    textAlign: "left",
    fontFamily: "inherit",
    color: ChromeTokens.color.ink,
    fontSize: 14,
    fontWeight: active ? 700 : 600,
  };
}

const contentStyle: CSSProperties = {
  flex: 1,
  overflowY: "auto",
  padding: "20px 28px 28px",
  color: ChromeTokens.color.ink,
  lineHeight: 1.55,
};

const contentCompactStyle: CSSProperties = {
  ...contentStyle,
  padding: "16px 18px 24px",
};

const ledeStyle: CSSProperties = {
  margin: 0,
  fontSize: 15,
  color: ChromeTokens.color.inkMuted,
};

const updatedStyle: CSSProperties = {
  margin: "16px 0 0",
  fontSize: 13,
  color: ChromeTokens.color.inkMuted,
  fontWeight: 600,
};

const sectionHStyle: CSSProperties = {
  margin: "0 0 8px",
  fontFamily: ChromeTokens.font.display,
  fontSize: 18,
  fontWeight: 700,
  color: ChromeTokens.color.ink,
};

const sectionPStyle: CSSProperties = {
  margin: "0 0 10px",
  fontSize: 14.5,
  color: ChromeTokens.color.ink,
};

const ulStyle: CSSProperties = {
  margin: "0 0 10px",
  paddingLeft: 22,
};

const liStyle: CSSProperties = {
  marginBottom: 6,
  fontSize: 14.5,
  color: ChromeTokens.color.ink,
};

const footerStyle: CSSProperties = {
  marginTop: 32,
  paddingTop: 16,
  borderTop: `1px solid ${ChromeTokens.color.baseMuted}`,
  fontSize: 13,
  color: ChromeTokens.color.inkMuted,
};
